import { useEffect, useMemo, useState } from 'react'
import { ShieldCheck, Users } from 'lucide-react'
import DataTable from '../../components/DataTable'
import Drawer from '../../components/Drawer'
import ExportMenu from '../../components/ExportMenu'
import FilterBar from '../../components/FilterBar'
import { InfoNote } from '../../components/Form'
import SegmentedControl from '../../components/SegmentedControl'
import { useToast } from '../../components/toastContext'
import { exportPolicyUsers, listPolicyUsers, listPolicyUsersNotViewed } from './engagementService'
import { policiesStrings as S } from './strings'
import './engagement.css'

/**
 * One policy, every user it was issued to — the legacy `/UsersPoliciesDetails` screen.
 *
 * That screen was a policies table with a second table underneath it, redrawn whenever a
 * row was clicked. Here the policy list stays where it is and its users open beside it, so
 * comparing two policies is two clicks rather than a scroll each time.
 *
 * WHY THE FILTER IS CLIENT-SIDE AND THE EXPORT IS NOT. `GetDetails` takes a policy id and
 * nothing else — no search, no status — while `Export` takes both. So the table narrows
 * here and the export is told the same thing, or the file would not match the screen.
 */
export default function PolicyUsersDrawer({ open, policy, onClose }) {
  const toast = useToast()

  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [emptyNote, setEmptyNote] = useState('')
  const [query, setQuery] = useState('')
  /** `null` is everyone; the other two mirror what `Export` calls `isViewed`. */
  const [seen, setSeen] = useState(null)
  const [busy, setBusy] = useState(false)

  /* Opening the drawer resets the scope. SEPARATE from the load effect below, which now
     depends on `seen` — resetting it in there would set state the effect watches, and the
     two would drive each other round for ever. */
  useEffect(() => {
    if (!open) return
    setQuery('')
    setSeen(null)
  }, [open, policy])

  useEffect(() => {
    if (!open || !policy) return undefined

    let cancelled = false
    setLoading(true)

    /* THE THIRD SCOPE IS A DIFFERENT QUESTION, so it is a different request. `GetDetails`
       returns only users who already have a `UserPolicyDetail` row, so nobody who has never
       opened the policy is in it — filtering that list for "not viewed" could never find
       them. `GetDetailsNotViewed` asks the opposite question. */
    const load = seen === false ? listPolicyUsersNotViewed : listPolicyUsers

    load(policy.id)
      .then((response) => {
        if (cancelled) return
        setRows(response.rows)
        setEmptyNote(response.message)
        setLoading(false)
      })
      .catch((error) => {
        if (cancelled) return
        setRows([])
        setLoading(false)
        toast.error(error?.message || S.users.exportFailed)
      })

    return () => {
      cancelled = true
    }
  }, [open, policy, toast, seen])

  /**
   * "Seen it" is the acceptance cell being anything other than the not-yet wording, which
   * the server sends already localized and differently for a read-only policy. Comparing
   * against the dates instead keeps this out of the wording: a user who has acknowledged
   * or viewed has an acceptance date, and one who has not does not.
   */
  const visible = useMemo(() => {
    /* `seen === false` is served by its OWN endpoint above, already scoped — re-filtering it
       here on `acceptDate` would empty it again, since those users have no detail row. */
    if (seen === null || seen === false) return rows
    return rows.filter((row) => Boolean(row.acceptDate))
  }, [rows, seen])

  const columns = useMemo(
    () => [
      {
        key: 'name',
        header: S.users.columns.name,
        render: (row) => <bdi>{row.name}</bdi>,
      },
      { key: 'version', header: S.users.columns.version, align: 'center' },
      {
        key: 'downloadStatus',
        // Already localized server-side, both of these.
        header: S.users.columns.download,
        render: (row) => <bdi>{row.downloadStatus}</bdi>,
      },
      {
        key: 'downloadDate',
        header: S.users.columns.downloadDate,
        render: (row) => (
          <span className="ud__date" dir="ltr">
            {row.downloadDate}
          </span>
        ),
      },
      {
        key: 'acceptStatus',
        header: S.users.columns.accept,
        render: (row) => <bdi>{row.acceptStatus}</bdi>,
      },
      {
        key: 'acceptDate',
        header: S.users.columns.acceptDate,
        render: (row) => (
          <span className="ud__date" dir="ltr">
            {row.acceptDate}
          </span>
        ),
      },
    ],
    [],
  )

  const runExport = async (format) => {
    setBusy(true)
    const outcome = await exportPolicyUsers({
      policyId: policy.id,
      format,
      // The export is told exactly what the table is showing.
      isViewed: seen,
      search: query,
    })
    setBusy(false)
    if (!outcome.ok) toast.error(outcome.message || S.users.exportFailed)
  }

  return (
    <Drawer
      dialog
      open={open}
      size="lg"
      title={policy?.name ?? S.users.title}
      icon={Users}
      subtitle={policy?.nameEn}
      onClose={onClose}
      footer={
        <ExportMenu
          onExport={busy ? () => {} : runExport}
          labels={{ excel: S.users.exportExcel, pdf: S.users.exportPdf }}
        />
      }
    >
      <div className="group-drawer">
        {emptyNote && !loading && rows.length === 0 && (
          <InfoNote icon={ShieldCheck}>
            {/* The server's own sentence for "nobody has this policy". */}
            <bdi>{emptyNote}</bdi>
          </InfoNote>
        )}

        <SegmentedControl
          items={[
            { id: 'all', label: S.users.allUsers, count: rows.length },
            { id: 'seen', label: S.users.viewed },
            { id: 'unseen', label: S.users.notViewed },
          ]}
          value={seen === null ? 'all' : seen ? 'seen' : 'unseen'}
          onChange={(id) => setSeen(id === 'all' ? null : id === 'seen')}
          ariaLabel={S.users.title}
        />

        <FilterBar
          search={{ value: query, onChange: setQuery, placeholder: S.users.searchPlaceholder }}
        />

        <DataTable
          /* A list inside a sheet is opened to answer one question; it starts at the top. */
          stateKey={false}
          columns={columns}
          rows={visible}
          loading={loading}
          query={query}
          controls={false}
          getRowId={(row) => row.id}
          emptyMessage={S.users.empty}
        />
      </div>
    </Drawer>
  )
}
